<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-slate-50 dark:bg-gray-900/50">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-3 sm:mb-5 mt-1 scroll-reveal">
      <div class="animate-fadeIn w-full sm:w-auto">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-indigo-600 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-indigo-600 tracking-widest">Panel de Control</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-2">
          Cotizaciones
          <span class="px-2 sm:px-3 py-0.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 text-xs sm:text-sm font-black rounded-lg border border-indigo-200 dark:border-indigo-800">
            {{ cotizaciones.length }}
          </span>
        </h1>
        <p class="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-0.5">Gestión de cotizaciones a clientes</p>
      </div>
      <div class="flex items-center gap-2 w-full sm:w-auto">
        <button @click="imprimirReporte" class="px-3 sm:px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-black text-[10px] sm:text-xs tracking-widest transition-all shadow-md flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
          <Icon icon="mdi:printer" class="text-base sm:text-lg" />
          <span class="hidden sm:inline">Imprimir Reporte</span>
          <span class="sm:hidden">Reporte</span>
        </button>
        <router-link to="/cotizaciones/nueva" class="px-3 sm:px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black text-[10px] sm:text-xs tracking-widest transition-all shadow-md flex items-center gap-2">
          <Icon icon="mdi:plus-circle" class="text-base sm:text-lg" />
          <span class="hidden sm:inline">Nueva Cotización</span>
          <span class="sm:hidden">Nueva</span>
        </router-link>
      </div>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-5 mb-3 sm:mb-5 animate-fadeInUp">
      <div class="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-5 items-end">
        <div class="sm:col-span-7 lg:col-span-8">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Búsqueda</label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg sm:text-2xl group-focus-within:text-indigo-500 transition-colors" />
            <input
              v-model="filtroBusqueda"
              type="text"
              placeholder="Buscar por número o cliente..."
              class="w-full pl-10 sm:pl-14 pr-3 sm:pr-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 focus:bg-white dark:focus:bg-gray-700 rounded-xl transition-all duration-300 outline-none dark:text-white font-bold text-sm sm:text-base"
            />
          </div>
        </div>
        
        <div class="sm:col-span-3 lg:col-span-2">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Estado</label>
          <select
            v-model="filtroEstado"
            class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer text-sm sm:text-base"
          >
            <option value="all">TODOS</option>
            <option value="borrador">BORRADOR</option>
            <option value="enviada">ENVIADA</option>
            <option value="aprobada">APROBADA</option>
            <option value="rechazada">RECHAZADA</option>
            <option value="expirada">EXPIRADA</option>
            <option value="convertida">CONVERTIDA</option>
          </select>
        </div>

        <div class="sm:col-span-2 lg:col-span-2">
          <button @click="cargarDatos" class="w-full py-2.5 sm:py-3.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-black text-[10px] sm:text-xs tracking-widest hover:bg-gray-200 dark:hover:bg-gray-600 transition-all flex items-center justify-center gap-2 border border-gray-200 dark:border-gray-600 shadow-sm">
            <Icon icon="mdi:refresh" :class="{'animate-spin': cargando}" class="text-lg sm:text-xl" />
            <span>Actualizar</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Stats -->
    <div class="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3 mb-3 sm:mb-5">
      <div class="bg-white dark:bg-gray-800 rounded-xl p-2.5 sm:p-4 shadow-sm border border-gray-200 dark:border-gray-700">
        <p class="text-[8px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest">Total</p>
        <p class="text-base sm:text-2xl font-black text-gray-800 dark:text-white">{{ cotizaciones.length }}</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-xl p-2.5 sm:p-4 shadow-sm border border-gray-200 dark:border-gray-700">
        <p class="text-[8px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest">Borrador</p>
        <p class="text-base sm:text-2xl font-black text-slate-500">{{ contarPorEstado('borrador') }}</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-xl p-2.5 sm:p-4 shadow-sm border border-gray-200 dark:border-gray-700">
        <p class="text-[8px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest">Enviadas</p>
        <p class="text-base sm:text-2xl font-black text-blue-600">{{ contarPorEstado('enviada') }}</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-xl p-2.5 sm:p-4 shadow-sm border border-gray-200 dark:border-gray-700">
        <p class="text-[8px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest">Aprobadas</p>
        <p class="text-base sm:text-2xl font-black text-emerald-600">{{ contarPorEstado('aprobada') }}</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-xl p-2.5 sm:p-4 shadow-sm border border-gray-200 dark:border-gray-700">
        <p class="text-[8px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest">Convertidas</p>
        <p class="text-base sm:text-2xl font-black text-purple-600">{{ contarPorEstado('convertida') }}</p>
      </div>
    </div>

    <!-- Tabla Desktop -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden animate-fadeInUp delay-100">
      <div class="hidden lg:block overflow-x-auto custom-scrollbar">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">N°</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Cliente</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Fecha</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Validez</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-right">Total USD</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Estado</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-right">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-if="cargando" v-for="i in 5" :key="'load-'+i" class="animate-pulse">
              <td colspan="7" class="px-5 py-4"><div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-full"></div></td>
            </tr>
            <tr v-if="!cargando && cotizacionesFiltradas.length === 0">
              <td colspan="7" class="px-5 py-16 text-center">
                <div class="flex flex-col items-center opacity-30 select-none">
                  <Icon icon="mdi:file-document-outline" class="text-7xl mb-3" />
                  <h4 class="text-xl font-black text-gray-400 tracking-widest">No hay cotizaciones</h4>
                </div>
              </td>
            </tr>
            <tr v-for="item in paginatedCotizaciones" :key="item.idCotizacion" class="hover:bg-indigo-50/30 dark:hover:bg-gray-700/30 transition-all group">
              <td class="px-5 py-4">
                <span class="px-2.5 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg font-black text-xs border border-indigo-100 dark:border-indigo-800 transition-transform inline-block">
                  {{ item.numeroCotizacion }}
                </span>
              </td>
              <td class="px-5 py-4">
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center font-black text-gray-500 text-xs">
                    {{ (item.clienteNombre || '?').charAt(0) }}
                  </div>
                  <div>
                    <p class="font-black text-gray-900 dark:text-white text-xs leading-tight">{{ item.clienteNombre || 'N/A' }}</p>
                    <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">{{ item.clienteDocumento || '' }}</p>
                  </div>
                </div>
              </td>
              <td class="px-5 py-4 text-center">
                <span class="text-xs text-gray-600 dark:text-gray-300 font-bold">{{ formatDate(item.fechaEmision) }}</span>
              </td>
              <td class="px-5 py-4 text-center">
                <span class="text-xs text-gray-600 dark:text-gray-300 font-bold">{{ formatDate(item.fechaValidez) }}</span>
              </td>
              <td class="px-5 py-4 text-right">
                <span class="text-sm font-black text-emerald-600 dark:text-emerald-400">${{ item.totalUsd?.toFixed(2) || '0.00' }}</span>
              </td>
              <td class="px-5 py-4 text-center">
                <span :class="getEstadoClass(item.estado)" class="px-3 py-1 rounded-lg text-[10px] font-black uppercase border shadow-sm inline-block">
                  {{ getEstadoLabel(item.estado) }}
                </span>
              </td>
              <td class="px-5 py-4">
                <div class="flex items-center justify-end gap-0.5 flex-nowrap whitespace-nowrap">
                  <button @click="descargarPDF(item)" class="p-1.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg hover:bg-emerald-600 hover:text-white transition-all shadow-sm flex-shrink-0" title="Descargar PDF">
                    <Icon icon="mdi:file-pdf-box" class="text-base" />
                  </button>
                  <button @click="verDetalle(item)" class="p-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-600 hover:text-white transition-all shadow-sm flex-shrink-0" title="Ver detalle">
                    <Icon icon="mdi:eye" class="text-base" />
                  </button>
                  <button v-if="item.estado === 'borrador'" @click="abrirModalEdicion(item)" class="p-1.5 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg hover:bg-amber-600 hover:text-white transition-all shadow-sm flex-shrink-0" title="Editar">
                    <Icon icon="mdi:pencil" class="text-base" />
                  </button>
                  <button v-if="item.estado === 'borrador'" @click="eliminarCotizacionItem(item)" class="p-1.5 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-600 hover:text-white transition-all shadow-sm flex-shrink-0" title="Eliminar">
                    <Icon icon="mdi:delete" class="text-base" />
                  </button>
                  <button v-if="item.estado === 'borrador'" @click="enviarCotizacionItem(item)" class="p-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-600 hover:text-white transition-all shadow-sm flex-shrink-0" title="Enviar">
                    <Icon icon="mdi:send" class="text-base" />
                  </button>
                  <button v-if="item.estado === 'enviada'" @click="aprobarCotizacionItem(item)" class="p-1.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg hover:bg-emerald-600 hover:text-white transition-all shadow-sm flex-shrink-0" title="Aprobar">
                    <Icon icon="mdi:check-circle" class="text-base" />
                  </button>
                  <button v-if="item.estado === 'enviada'" @click="rechazarCotizacionItem(item)" class="p-1.5 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-600 hover:text-white transition-all shadow-sm flex-shrink-0" title="Rechazar">
                    <Icon icon="mdi:close-circle" class="text-base" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Versión Mobile: Tarjetas -->
      <div class="lg:hidden space-y-3 p-2">
        <div v-if="cargando" v-for="i in 3" :key="'load-mobile-'+i" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
          <div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-full mb-2"></div>
          <div class="h-3 bg-gray-100 dark:bg-gray-700 rounded w-3/4"></div>
        </div>
        
        <div v-if="!cargando && cotizacionesFiltradas.length === 0" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
          <div class="flex flex-col items-center opacity-30 select-none">
            <Icon icon="mdi:file-document-outline" class="text-6xl mb-3" />
            <h4 class="text-lg font-black text-gray-400 tracking-widest">No hay cotizaciones</h4>
          </div>
        </div>
        
        <div v-for="item in paginatedCotizaciones" :key="item.idCotizacion" 
             class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 p-4 hover:shadow-lg transition-all duration-200">
          
          <div class="flex flex-wrap items-center justify-between gap-2 mb-3 pb-2 border-b border-gray-100 dark:border-gray-700">
            <div class="flex items-center gap-2">
              <span class="px-2.5 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg font-black text-xs border border-indigo-100 dark:border-indigo-800">
                {{ item.numeroCotizacion }}
              </span>
              <span :class="getEstadoClass(item.estado)" class="px-2 py-0.5 rounded-lg text-[8px] font-black uppercase border shadow-sm">
                {{ getEstadoLabel(item.estado) }}
              </span>
            </div>
            <div class="text-[9px] text-gray-400 font-black">
              {{ formatDate(item.fechaEmision) }}
            </div>
          </div>

          <div class="flex items-center gap-2 mb-2">
            <div class="w-7 h-7 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center font-black text-gray-500 text-xs flex-shrink-0">
              {{ (item.clienteNombre || '?').charAt(0) }}
            </div>
            <div class="flex-1 min-w-0">
              <p class="font-black text-gray-900 dark:text-white text-xs leading-tight truncate">{{ item.clienteNombre || 'N/A' }}</p>
              <p class="text-[8px] font-black text-gray-400 uppercase tracking-widest">{{ item.clienteDocumento || '' }}</p>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-1 mb-2 text-[10px]">
            <div class="flex items-center gap-1">
              <Icon icon="mdi:calendar" class="text-gray-400 text-sm" />
              <span class="font-bold text-gray-600 dark:text-gray-400">Val: {{ formatDate(item.fechaValidez) }}</span>
            </div>
            <div class="flex items-center gap-1 justify-end">
              <span class="font-black text-emerald-600 dark:text-emerald-400">${{ item.totalUsd?.toFixed(2) || '0.00' }}</span>
            </div>
          </div>

          <div class="flex flex-wrap items-center gap-1.5 pt-2 border-t border-gray-200 dark:border-gray-700">
            <button @click="descargarPDF(item)" class="p-1.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg hover:bg-emerald-600 hover:text-white transition-all shadow-sm" title="PDF">
              <Icon icon="mdi:file-pdf-box" class="text-sm" />
            </button>
            <button @click="verDetalle(item)" class="p-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-600 hover:text-white transition-all shadow-sm" title="Ver">
              <Icon icon="mdi:eye" class="text-sm" />
            </button>
            <button v-if="item.estado === 'borrador'" @click="abrirModalEdicion(item)" class="p-1.5 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg hover:bg-amber-600 hover:text-white transition-all shadow-sm" title="Editar">
              <Icon icon="mdi:pencil" class="text-sm" />
            </button>
            <button v-if="item.estado === 'borrador'" @click="eliminarCotizacionItem(item)" class="p-1.5 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-600 hover:text-white transition-all shadow-sm" title="Eliminar">
              <Icon icon="mdi:delete" class="text-sm" />
            </button>
            <button v-if="item.estado === 'borrador'" @click="enviarCotizacionItem(item)" class="p-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-600 hover:text-white transition-all shadow-sm" title="Enviar">
              <Icon icon="mdi:send" class="text-sm" />
            </button>
            <button v-if="item.estado === 'enviada'" @click="aprobarCotizacionItem(item)" class="p-1.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg hover:bg-emerald-600 hover:text-white transition-all shadow-sm" title="Aprobar">
              <Icon icon="mdi:check-circle" class="text-sm" />
            </button>
            <button v-if="item.estado === 'enviada'" @click="rechazarCotizacionItem(item)" class="p-1.5 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-600 hover:text-white transition-all shadow-sm" title="Rechazar">
              <Icon icon="mdi:close-circle" class="text-sm" />
            </button>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="cotizacionesFiltradas.length > 0" class="bg-gray-50/50 dark:bg-gray-800/50 px-3 sm:px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div class="text-[10px] sm:text-[11px] font-black text-gray-400 uppercase tracking-widest text-center sm:text-left">
          MOSTRANDO {{ (currentPage - 1) * itemsPerPage + 1 }} - {{ Math.min(currentPage * itemsPerPage, cotizacionesFiltradas.length) }} DE {{ cotizacionesFiltradas.length }}
        </div>
        <div class="flex items-center gap-1 flex-wrap justify-center">
          <button @click="goToFirstPage" :disabled="currentPage === 1" :class="['p-1.5 rounded-lg border transition-all hidden sm:inline-flex', currentPage === 1 ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-double-left" class="text-base sm:text-lg" />
          </button>
          <button @click="currentPage = Math.max(currentPage - 1, 1)" :disabled="currentPage === 1" :class="['p-1.5 rounded-lg border transition-all', currentPage === 1 ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-left" class="text-base sm:text-2xl" />
          </button>
          <div class="hidden sm:flex items-center gap-0.5 sm:gap-1 mx-1 sm:mx-2">
            <button v-for="page in displayedPages" :key="page" @click="currentPage = page" :class="['w-7 h-7 sm:w-9 sm:h-9 rounded-lg text-[10px] sm:text-xs font-black transition-all flex items-center justify-center', currentPage === page ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/30' : 'text-gray-500 hover:bg-white dark:hover:bg-gray-700 border border-transparent hover:border-gray-200 dark:hover:border-gray-600']">
              {{ page }}
            </button>
          </div>
          <span class="sm:hidden text-xs font-black text-gray-600 dark:text-gray-300 px-2">{{ currentPage }} / {{ totalPages }}</span>
          <button @click="currentPage = Math.min(currentPage + 1, totalPages)" :disabled="currentPage === totalPages" :class="['p-1.5 rounded-lg border transition-all', currentPage === totalPages ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-right" class="text-base sm:text-2xl" />
          </button>
          <button @click="goToLastPage" :disabled="currentPage === totalPages" :class="['p-1.5 rounded-lg border transition-all hidden sm:inline-flex', currentPage === totalPages ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-double-right" class="text-base sm:text-lg" />
          </button>
        </div>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- MODAL DE EDICIÓN -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="modalEdicionVisible" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9998]" @click.self="cerrarModalEdicion">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl animate-zoomIn max-h-[90vh] flex flex-col">
          <div class="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 sm:px-6 py-4 flex items-center gap-3 flex-shrink-0 rounded-t-2xl">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon icon="mdi:pencil-box" class="text-white text-xl" />
            </div>
            <div>
              <h3 class="text-sm font-black text-white">Editar Cotización</h3>
              <p class="text-[10px] text-white/70 font-bold">{{ formEdicion.numeroCotizacion || 'Nueva' }}</p>
            </div>
            <button @click="cerrarModalEdicion" class="ml-auto p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg" />
            </button>
          </div>
          
          <div class="p-4 sm:p-6 overflow-y-auto flex-1 custom-scrollbar">
            <form @submit.prevent="guardarEdicion" class="space-y-4">
              <!-- Datos del Cliente -->
              <div class="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
                <h3 class="text-[10px] font-black text-gray-400 uppercase mb-3 flex items-center gap-2">
                  <Icon icon="mdi:account" class="text-indigo-500" />
                  Datos del Cliente
                </h3>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">Cliente *</label>
                    <div class="flex gap-2">
                      <input 
                        type="text" 
                        readonly
                        :value="clienteSeleccionado ? `${clienteSeleccionado.nombre} ${clienteSeleccionado.apellido} - ${clienteSeleccionado.documento}` : 'Seleccionar cliente...'"
                        class="flex-1 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-xl px-3 sm:px-4 py-2.5 text-xs text-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 transition-all font-bold cursor-pointer"
                        @click="abrirModalClienteEdicion"
                      />
                      <button type="button" @click="abrirModalClienteEdicion" class="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2.5 rounded-xl font-bold transition-all text-xs flex items-center gap-1 flex-shrink-0">
                        <Icon icon="mdi:magnify" /> Buscar
                      </button>
                    </div>
                  </div>
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">Fecha de Validez *</label>
                    <input v-model="formEdicion.fechaValidez" type="date" required class="w-full bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-xl px-3 sm:px-4 py-2.5 text-xs text-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 transition-all font-bold" />
                  </div>
                </div>
              </div>

              <!-- Detalles -->
              <div class="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
                <div class="flex justify-between items-center mb-3">
                  <h3 class="text-[10px] font-black text-gray-400 uppercase flex items-center gap-2">
                    <Icon icon="mdi:format-list-bulleted" class="text-indigo-500" />
                    Items
                    <span class="text-[10px] bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 px-2 py-0.5 rounded-full ml-1">{{ formEdicion.detalles.length }}</span>
                  </h3>
                  <button type="button" @click="agregarItemEdicion" class="text-indigo-600 hover:text-indigo-700 text-xs font-bold flex items-center gap-1">
                    <Icon icon="mdi:plus-circle" /> Agregar
                  </button>
                </div>

                <div v-for="(item, index) in formEdicion.detalles" :key="index" class="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 mb-3 border border-gray-200 dark:border-gray-600">
                  <div class="flex justify-between items-start">
                    <h4 class="text-sm font-bold text-gray-700 dark:text-white">Item #{{ index + 1 }}</h4>
                    <button type="button" @click="eliminarItemEdicion(index)" class="text-rose-500 hover:text-rose-600">
                      <Icon icon="mdi:close" class="text-lg" />
                    </button>
                  </div>

                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    <div>
                      <label class="block text-[10px] font-black text-gray-400 uppercase mb-1 ml-1">Tipo</label>
                      <select v-model="item.tipoItem" class="w-full bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 text-xs text-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 transition-all font-bold">
                        <option value="producto">Producto</option>
                        <option value="servicio">Servicio</option>
                        <option value="producto_servicio">Especial</option>
                      </select>
                    </div>

                    <div v-if="item.tipoItem === 'producto'">
                      <label class="block text-[10px] font-black text-gray-400 uppercase mb-1 ml-1">Producto</label>
                      <div class="flex gap-2">
                        <input 
                          type="text" 
                          readonly
                          :value="item.productoSeleccionado ? `${item.productoSeleccionado.nombre} - $${item.productoSeleccionado.precioVentaUsd || item.productoSeleccionado.precio_venta_usd || 0}` : 'Seleccionar...'"
                          class="flex-1 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 text-xs text-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 transition-all font-bold cursor-pointer"
                          @click="abrirModalProductoEdicion(index)"
                        />
                        <button type="button" @click="abrirModalProductoEdicion(index)" class="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-xl font-bold transition-all text-xs flex items-center gap-1 flex-shrink-0">
                          <Icon icon="mdi:magnify" />
                        </button>
                      </div>
                    </div>

                    <div v-else-if="item.tipoItem === 'servicio'">
                      <label class="block text-[10px] font-black text-gray-400 uppercase mb-1 ml-1">Servicio</label>
                      <div class="flex gap-2">
                        <input 
                          type="text" 
                          readonly
                          :value="item.servicioSeleccionado ? `${item.servicioSeleccionado.nombre} - $${item.servicioSeleccionado.precioUsd || item.servicioSeleccionado.precio_usd || 0}` : 'Seleccionar...'"
                          class="flex-1 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 text-xs text-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 transition-all font-bold cursor-pointer"
                          @click="abrirModalServicioEdicion(index)"
                        />
                        <button type="button" @click="abrirModalServicioEdicion(index)" class="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-xl font-bold transition-all text-xs flex items-center gap-1 flex-shrink-0">
                          <Icon icon="mdi:magnify" />
                        </button>
                      </div>
                    </div>

                    <div v-else>
                      <label class="block text-[10px] font-black text-gray-400 uppercase mb-1 ml-1">Especial</label>
                      <select v-model="item.idProductoServicios" class="w-full bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 text-xs text-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 transition-all font-bold">
                        <option :value="null">Seleccionar...</option>
                        <option v-for="ps in productosServicios" :key="ps.idProductoServicios" :value="ps.idProductoServicios">
                          {{ ps.nombre }}
                        </option>
                      </select>
                    </div>
                  </div>

                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                    <div>
                      <label class="block text-[10px] font-black text-gray-400 uppercase mb-1 ml-1">Descripción</label>
                      <input v-model="item.descripcionManual" type="text" placeholder="Descripción..." class="w-full bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 text-xs text-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 transition-all font-bold" />
                    </div>
                    <div>
                      <label class="block text-[10px] font-black text-gray-400 uppercase mb-1 ml-1">Precio Unitario USD *</label>
                      <input v-model.number="item.precioUnitarioUsd" type="number" step="0.01" min="0" required class="w-full bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 text-xs text-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 transition-all font-bold" />
                    </div>
                  </div>

                  <div class="grid grid-cols-3 gap-3 mt-2">
                    <div>
                      <label class="block text-[10px] font-black text-gray-400 uppercase mb-1 ml-1">Cantidad</label>
                      <input 
                        v-model.number="item.cantidad" 
                        type="number" 
                        min="1" 
                        step="1" 
                        class="w-full bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 text-xs text-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 transition-all font-bold"
                      />
                    </div>
                    <div>
                      <label class="block text-[10px] font-black text-gray-400 uppercase mb-1 ml-1">Descuento %</label>
                      <input 
                        v-model.number="item.descuentoPorcentaje" 
                        type="number" 
                        min="0" 
                        max="100" 
                        step="0.01" 
                        class="w-full bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 text-xs text-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 transition-all font-bold"
                      />
                    </div>
                    <div>
                      <label class="block text-[10px] font-black text-gray-400 uppercase mb-1 ml-1">Garantía (meses)</label>
                      <input 
                        v-model.number="item.garantiaMeses" 
                        type="number" 
                        min="0" 
                        class="w-full bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 text-xs text-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 transition-all font-bold"
                      />
                    </div>
                  </div>

                  <div class="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700 flex justify-end">
                    <div class="text-right">
                      <p class="text-[8px] text-gray-400 uppercase tracking-wider">Subtotal</p>
                      <p class="text-sm font-black text-emerald-600 dark:text-emerald-400">
                        ${{ ((item.precioUnitarioUsd || 0) * (item.cantidad || 1)).toFixed(2) }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Datos adicionales -->
              <div class="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
                <h3 class="text-[10px] font-black text-gray-400 uppercase mb-3 flex items-center gap-2">
                  <Icon icon="mdi:information" class="text-indigo-500" />
                  Datos Adicionales
                </h3>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">Forma de Pago</label>
                    <input v-model="formEdicion.formaPago" type="text" placeholder="Ej: Efectivo, Transferencia..." class="w-full bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-xl px-3 sm:px-4 py-2.5 text-xs text-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 transition-all font-bold" />
                  </div>
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">Condiciones de Pago</label>
                    <input v-model="formEdicion.condicionesPago" type="text" placeholder="Condiciones especiales..." class="w-full bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-xl px-3 sm:px-4 py-2.5 text-xs text-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 transition-all font-bold" />
                  </div>
                </div>
                <div class="mt-3">
                  <label class="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">Observaciones</label>
                  <textarea v-model="formEdicion.observaciones" rows="2" placeholder="Observaciones generales..." class="w-full bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-xl px-3 sm:px-4 py-2.5 text-xs text-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 transition-all font-bold resize-none"></textarea>
                </div>
              </div>
            </form>
          </div>

          <div class="px-4 sm:px-6 pb-6 flex flex-col sm:flex-row gap-3 flex-shrink-0 rounded-b-2xl bg-white dark:bg-gray-800">
            <button @click="cerrarModalEdicion" class="w-full sm:flex-1 py-3 border-2 border-gray-100 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs tracking-widest hover:bg-gray-50 dark:hover:bg-gray-700 transition-all">Cancelar</button>
            <button @click="guardarEdicion" :disabled="guardandoEdicion" class="w-full sm:flex-1 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:opacity-90 transition-all shadow-lg shadow-indigo-500/30 disabled:opacity-50 flex items-center justify-center gap-2">
              <Icon :icon="guardandoEdicion ? 'mdi:loading' : 'mdi:content-save'" :class="{'animate-spin': guardandoEdicion}" />
              {{ guardandoEdicion ? 'Guardando...' : 'Guardar Cambios' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL CLIENTE (Búsqueda) -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="modalClienteVisible" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]" @click.self="modalClienteVisible = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-zoomIn">
          <div class="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 sm:px-6 py-4 flex items-center gap-3 flex-shrink-0 rounded-t-2xl">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon icon="mdi:account-search" class="text-white text-xl" />
            </div>
            <div class="flex-1">
              <h3 class="text-sm font-black text-white">Seleccionar Cliente</h3>
              <p class="text-[10px] text-white/70 font-bold">
                {{ clientes.length }} cliente{{ clientes.length !== 1 ? 's' : '' }} disponibles
              </p>
            </div>
            <button @click="modalClienteVisible = false" class="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg" />
            </button>
          </div>
          <div class="p-4 flex flex-col flex-1 overflow-hidden">
            <div class="relative mb-4 flex-shrink-0">
              <Icon icon="mdi:magnify" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input v-model="busquedaCliente" type="text" placeholder="Buscar por nombre, documento o teléfono..." class="w-full pl-11 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 transition-all font-bold" />
            </div>

            <div class="overflow-y-auto flex-1 custom-scrollbar">
              <!-- Loading -->
              <div v-if="cargandoClientesModal" class="text-center py-10">
                <div class="inline-block animate-spin rounded-full h-8 w-8 border-3 border-indigo-500 border-t-transparent"></div>
                <p class="text-sm text-gray-500 mt-3 font-bold">Cargando clientes...</p>
              </div>

              <!-- Sin resultados -->
              <div v-else-if="clientesFiltrados.length === 0" class="text-center py-10">
                <Icon icon="mdi:account-off" class="text-5xl text-gray-300 mx-auto mb-3" />
                <p class="text-gray-600 dark:text-gray-400 font-bold text-sm">
                  {{ busquedaCliente ? `No se encontraron clientes con "${busquedaCliente}"` : 'No hay clientes registrados' }}
                </p>
                <p v-if="!busquedaCliente" class="text-xs text-gray-400 mt-1">
                  Debes crear clientes primero desde el módulo de Clientes
                </p>
                <button v-if="busquedaCliente" @click="busquedaCliente = ''" class="mt-3 text-xs text-indigo-600 hover:text-indigo-700 font-bold">
                  Limpiar búsqueda
                </button>
              </div>

              <!-- Lista -->
              <div v-else class="space-y-1">
                <button
                  v-for="cliente in clientesFiltrados"
                  :key="cliente.idCliente"
                  type="button"
                  @click="seleccionarClienteEdicion(cliente)"
                  class="w-full text-left p-3 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-xl cursor-pointer transition-all border border-transparent hover:border-indigo-200 dark:hover:border-indigo-800/30"
                >
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-black text-sm flex-shrink-0">
                      {{ (cliente.nombre || '?').charAt(0).toUpperCase() }}
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="font-bold text-sm text-gray-800 dark:text-white truncate">
                        {{ cliente.nombre }} {{ cliente.apellido }}
                      </p>
                      <div class="text-xs text-gray-500 flex flex-wrap gap-2 sm:gap-3 mt-0.5">
                        <span>📄 {{ cliente.documento }}</span>
                        <span v-if="cliente.telefono">📞 {{ cliente.telefono }}</span>
                      </div>
                    </div>
                    <Icon icon="mdi:chevron-right" class="text-gray-400 text-lg flex-shrink-0" />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL PRODUCTO (Búsqueda) -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="modalProductoVisible" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]" @click.self="modalProductoVisible = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-zoomIn">
          <div class="bg-gradient-to-r from-emerald-600 to-teal-600 px-4 sm:px-6 py-4 flex items-center gap-3 flex-shrink-0 rounded-t-2xl">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon icon="mdi:package-search" class="text-white text-xl" />
            </div>
            <div class="flex-1">
              <h3 class="text-sm font-black text-white">Seleccionar Producto</h3>
              <p class="text-[10px] text-white/70 font-bold">
                {{ productos.length }} producto{{ productos.length !== 1 ? 's' : '' }} disponibles
              </p>
            </div>
            <button @click="modalProductoVisible = false" class="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg" />
            </button>
          </div>
          <div class="p-4 flex flex-col flex-1 overflow-hidden">
            <div class="relative mb-4 flex-shrink-0">
              <Icon icon="mdi:magnify" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input v-model="busquedaProducto" type="text" placeholder="Buscar por nombre o código..." class="w-full pl-11 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 transition-all font-bold" />
            </div>

            <div class="overflow-y-auto flex-1 custom-scrollbar">
              <!-- Loading -->
              <div v-if="cargandoProductosModal" class="text-center py-10">
                <div class="inline-block animate-spin rounded-full h-8 w-8 border-3 border-emerald-500 border-t-transparent"></div>
                <p class="text-sm text-gray-500 mt-3 font-bold">Cargando productos...</p>
              </div>

              <!-- Sin resultados -->
              <div v-else-if="productosFiltrados.length === 0" class="text-center py-10">
                <Icon icon="mdi:package-off" class="text-5xl text-gray-300 mx-auto mb-3" />
                <p class="text-gray-600 dark:text-gray-400 font-bold text-sm">
                  {{ busquedaProducto ? `No se encontraron productos con "${busquedaProducto}"` : 'No hay productos registrados' }}
                </p>
                <p v-if="!busquedaProducto" class="text-xs text-gray-400 mt-1">
                  Debes crear productos primero desde el módulo de Productos
                </p>
                <button v-if="busquedaProducto" @click="busquedaProducto = ''" class="mt-3 text-xs text-emerald-600 hover:text-emerald-700 font-bold">
                  Limpiar búsqueda
                </button>
              </div>

              <!-- Lista -->
              <div v-else class="space-y-1">
                <button
                  v-for="producto in productosFiltrados"
                  :key="producto.idProducto"
                  type="button"
                  @click="seleccionarProductoEdicion(producto)"
                  class="w-full text-left p-3 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-xl cursor-pointer transition-all border border-transparent hover:border-emerald-200 dark:hover:border-emerald-800/30"
                >
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white flex-shrink-0">
                      <Icon icon="mdi:package-variant" class="text-lg" />
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="font-bold text-sm text-gray-800 dark:text-white truncate">
                        {{ producto.nombre }}
                      </p>
                      <div class="text-xs text-gray-500 flex gap-3 mt-0.5">
                        <span v-if="producto.codigo">📦 {{ producto.codigo }}</span>
                        <span class="text-emerald-600 font-bold">${{ (producto.precioVentaUsd || producto.precio_venta_usd || 0).toFixed(2) }}</span>
                      </div>
                    </div>
                    <Icon icon="mdi:chevron-right" class="text-gray-400 text-lg flex-shrink-0" />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL SERVICIO (Búsqueda) -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="modalServicioVisible" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]" @click.self="modalServicioVisible = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-zoomIn">
          <div class="bg-gradient-to-r from-blue-600 to-cyan-600 px-4 sm:px-6 py-4 flex items-center gap-3 flex-shrink-0 rounded-t-2xl">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon icon="mdi:wrench-search" class="text-white text-xl" />
            </div>
            <div class="flex-1">
              <h3 class="text-sm font-black text-white">Seleccionar Servicio</h3>
              <p class="text-[10px] text-white/70 font-bold">
                {{ servicios.length }} servicio{{ servicios.length !== 1 ? 's' : '' }} disponibles
              </p>
            </div>
            <button @click="modalServicioVisible = false" class="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg" />
            </button>
          </div>
          <div class="p-4 flex flex-col flex-1 overflow-hidden">
            <div class="relative mb-4 flex-shrink-0">
              <Icon icon="mdi:magnify" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input v-model="busquedaServicio" type="text" placeholder="Buscar por nombre o código..." class="w-full pl-11 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 transition-all font-bold" />
            </div>

            <div class="overflow-y-auto flex-1 custom-scrollbar">
              <!-- Loading -->
              <div v-if="cargandoServiciosModal" class="text-center py-10">
                <div class="inline-block animate-spin rounded-full h-8 w-8 border-3 border-blue-500 border-t-transparent"></div>
                <p class="text-sm text-gray-500 mt-3 font-bold">Cargando servicios...</p>
              </div>

              <!-- Sin resultados -->
              <div v-else-if="serviciosFiltrados.length === 0" class="text-center py-10">
                <Icon icon="mdi:wrench-off" class="text-5xl text-gray-300 mx-auto mb-3" />
                <p class="text-gray-600 dark:text-gray-400 font-bold text-sm">
                  {{ busquedaServicio ? `No se encontraron servicios con "${busquedaServicio}"` : 'No hay servicios registrados' }}
                </p>
                <p v-if="!busquedaServicio" class="text-xs text-gray-400 mt-1">
                  Debes crear servicios primero desde el módulo de Servicios
                </p>
                <button v-if="busquedaServicio" @click="busquedaServicio = ''" class="mt-3 text-xs text-blue-600 hover:text-blue-700 font-bold">
                  Limpiar búsqueda
                </button>
              </div>

              <!-- Lista -->
              <div v-else class="space-y-1">
                <button
                  v-for="servicio in serviciosFiltrados"
                  :key="servicio.idServicio"
                  type="button"
                  @click="seleccionarServicioEdicion(servicio)"
                  class="w-full text-left p-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl cursor-pointer transition-all border border-transparent hover:border-blue-200 dark:hover:border-blue-800/30"
                >
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center text-white flex-shrink-0">
                      <Icon icon="mdi:wrench" class="text-lg" />
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="font-bold text-sm text-gray-800 dark:text-white truncate">
                        {{ servicio.nombre }}
                      </p>
                      <div class="text-xs text-gray-500 flex gap-3 mt-0.5">
                        <span v-if="servicio.codigo">🔧 {{ servicio.codigo }}</span>
                        <span class="text-emerald-600 font-bold">${{ (servicio.precioUsd || servicio.precio_usd || 0).toFixed(2) }}</span>
                      </div>
                    </div>
                    <Icon icon="mdi:chevron-right" class="text-gray-400 text-lg flex-shrink-0" />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { Icon } from '@iconify/vue';
import { useRouter } from 'vue-router';
import Swal from 'sweetalert2';
import {
  obtenerCotizaciones,
  eliminarCotizacion as eliminarCotizacionAPI,
  enviarCotizacion as enviarCotizacionAPI,
  aprobarCotizacion as aprobarCotizacionAPI,
  rechazarCotizacion as rechazarCotizacionAPI,
  actualizarCotizacion
} from '@/api/cotizacion';
import { obtenerClientes } from '@/api/clientes';
import { obtenerProductos } from '@/api/productos';
import { obtenerServicios } from '@/api/servicios';
import { obtenerProductosServicios as obtenerProductosServiciosAPI } from '@/api/productoServicio';
import { descargarCotizacionPDF } from '@/utils/reporteCotizacionGenerator';

const router = useRouter();
const cotizaciones = ref([]);
const cargando = ref(false);
const filtroBusqueda = ref('');
const filtroEstado = ref('all');

// Paginación
const currentPage = ref(1);
const itemsPerPage = 5;
const maxVisiblePages = 10;

// ============================================
// DATOS PARA MODALES
// ============================================
const clientes = ref([]);
const productos = ref([]);
const servicios = ref([]);
const productosServicios = ref([]);

// Estados de carga de modales
const cargandoClientesModal = ref(false);
const cargandoProductosModal = ref(false);
const cargandoServiciosModal = ref(false);

// Modal Edición
const modalEdicionVisible = ref(false);
const guardandoEdicion = ref(false);
const cotizacionEditando = ref(null);
const clienteSeleccionado = ref(null);

const formEdicion = ref({
  idCotizacion: null,
  idCliente: '',
  fechaValidez: '',
  formaPago: '',
  condicionesPago: '',
  observaciones: '',
  numeroCotizacion: '',
  detalles: []
});

// Modal Cliente
const modalClienteVisible = ref(false);
const busquedaCliente = ref('');

// Modal Producto
const modalProductoVisible = ref(false);
const busquedaProducto = ref('');
const itemIndexProducto = ref(null);

// Modal Servicio
const modalServicioVisible = ref(false);
const busquedaServicio = ref('');
const itemIndexServicio = ref(null);

// ============================================
// COMPUTED
// ============================================
const cotizacionesFiltradas = computed(() => {
  return cotizaciones.value.filter(item => {
    const matchBusqueda = 
      (item.numeroCotizacion || '').toLowerCase().includes(filtroBusqueda.value.toLowerCase()) ||
      (item.clienteNombre || '').toLowerCase().includes(filtroBusqueda.value.toLowerCase());
    const matchEstado = filtroEstado.value === 'all' || item.estado === filtroEstado.value;
    return matchBusqueda && matchEstado;
  });
});

const totalPages = computed(() => Math.ceil(cotizacionesFiltradas.value.length / itemsPerPage));

const paginatedCotizaciones = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  return cotizacionesFiltradas.value.slice(start, end);
});

const displayedPages = computed(() => {
  const total = totalPages.value;
  const max = maxVisiblePages;
  const current = currentPage.value;
  
  if (total <= max) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  
  let start = Math.max(1, current - Math.floor(max / 2));
  let end = start + max - 1;
  
  if (end > total) {
    end = total;
    start = Math.max(1, end - max + 1);
  }
  
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});

const clientesFiltrados = computed(() => {
  const termino = busquedaCliente.value.toLowerCase().trim();
  if (!termino) return clientes.value;
  return clientes.value.filter(c => 
    (c.nombre || '').toLowerCase().includes(termino) ||
    (c.apellido || '').toLowerCase().includes(termino) ||
    (c.documento || '').toLowerCase().includes(termino) ||
    (c.telefono || '').includes(termino)
  );
});

const productosFiltrados = computed(() => {
  const termino = busquedaProducto.value.toLowerCase().trim();
  if (!termino) return productos.value;
  return productos.value.filter(p => 
    (p.nombre || '').toLowerCase().includes(termino) ||
    (p.codigo || '').toLowerCase().includes(termino)
  );
});

const serviciosFiltrados = computed(() => {
  const termino = busquedaServicio.value.toLowerCase().trim();
  if (!termino) return servicios.value;
  return servicios.value.filter(s => 
    (s.nombre || '').toLowerCase().includes(termino) ||
    (s.codigo || '').toLowerCase().includes(termino)
  );
});

// ============================================
// PAGINACIÓN
// ============================================
const goToFirstPage = () => { currentPage.value = 1; };
const goToLastPage = () => { currentPage.value = totalPages.value; };

// ============================================
// MÉTODOS PRINCIPALES
// ============================================
const cargarDatos = async () => {
  cargando.value = true;
  try {
    const response = await obtenerCotizaciones();
    if (response.success) {
      cotizaciones.value = response.data || [];
      console.log('📋 Cotizaciones cargadas:', cotizaciones.value.length);
    } else {
      toast('Error al cargar cotizaciones', 'error');
    }
  } catch (error) {
    console.error('Error:', error);
  } finally {
    cargando.value = false;
  }
};

const cargarDatosMaestros = async () => {
  try {
    console.log('📋 Cargando datos maestros para edición...');
    
    const [clientesRes, productosRes, serviciosRes, psRes] = await Promise.all([
      obtenerClientes().catch(e => ({ success: false, error: e })),
      obtenerProductos('todos').catch(e => ({ success: false, error: e })),
      obtenerServicios('todos').catch(e => ({ success: false, error: e })),
      obtenerProductosServiciosAPI().catch(e => ({ success: false, error: e }))
    ]);
    
    if (clientesRes.success) {
      clientes.value = clientesRes.data || [];
      console.log('✅ Clientes cargados:', clientes.value.length);
    }
    if (productosRes.success) {
      productos.value = productosRes.data || [];
      console.log('✅ Productos cargados:', productos.value.length);
    }
    if (serviciosRes.success) {
      servicios.value = serviciosRes.data || [];
      console.log('✅ Servicios cargados:', servicios.value.length);
    }
    if (psRes && psRes.success) {
      productosServicios.value = psRes.data || [];
      console.log('✅ Productos/Servicios especiales cargados:', productosServicios.value.length);
    }
  } catch (error) {
    console.error('❌ Error cargando datos maestros:', error);
  }
};

const contarPorEstado = (estado) => {
  return cotizaciones.value.filter(c => c.estado === estado).length;
};

// ============================================
// PDF - DESCARGAR
// ============================================
const descargarPDF = async (item) => {
  if (!item) {
    toast('Cotización no encontrada', 'error');
    return;
  }
  
  try {
    const cotizacionCompleta = cotizaciones.value.find(c => c.idCotizacion === item.idCotizacion);
    if (cotizacionCompleta) {
      await descargarCotizacionPDF(cotizacionCompleta);
      toast('PDF descargado correctamente');
    } else {
      toast('No se pudo obtener los detalles de la cotización', 'error');
    }
  } catch (error) {
    console.error('Error al descargar PDF:', error);
    toast('Error al generar el PDF', 'error');
  }
};

// ============================================
// ACCIONES DE LA TABLA
// ============================================
const verDetalle = (item) => {
  if (!item) {
    toast('Error: Item inválido', 'error');
    return;
  }
  const id = item.idCotizacion || item.id;
  if (!id) {
    toast('Error: ID de cotización no válido', 'error');
    return;
  }
  router.push(`/cotizaciones/${id}`);
};

const eliminarCotizacionItem = async (item) => {
  const id = item.idCotizacion || item.id;
  if (!id) {
    toast('Error: ID de cotización no válido', 'error');
    return;
  }

  const result = await Swal.fire({
    title: '¿Estás seguro?',
    text: `Se eliminará la cotización ${item.numeroCotizacion}. Esta acción no se puede deshacer.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#f43f5e',
    cancelButtonColor: '#4f46e5',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  });

  if (result.isConfirmed) {
    try {
      const response = await eliminarCotizacionAPI(id);
      if (response.success) {
        toast('Cotización eliminada correctamente');
        await cargarDatos();
      } else {
        toast(response.error || 'Error al eliminar', 'error');
      }
    } catch (error) {
      toast('Error al eliminar', 'error');
    }
  }
};

const enviarCotizacionItem = async (item) => {
  const id = item.idCotizacion || item.id;
  if (!id) {
    toast('Error: ID de cotización no válido', 'error');
    return;
  }

  try {
    const response = await enviarCotizacionAPI(id);
    if (response.success) {
      toast('Cotización enviada correctamente');
      await cargarDatos();
    } else {
      toast(response.error || 'Error al enviar', 'error');
    }
  } catch (error) {
    toast('Error al enviar', 'error');
  }
};

const aprobarCotizacionItem = async (item) => {
  const id = item.idCotizacion || item.id;
  if (!id) {
    toast('Error: ID de cotización no válido', 'error');
    return;
  }

  try {
    const response = await aprobarCotizacionAPI(id);
    if (response.success) {
      toast('Cotización aprobada correctamente');
      await cargarDatos();
    } else {
      toast(response.error || 'Error al aprobar', 'error');
    }
  } catch (error) {
    toast('Error al aprobar', 'error');
  }
};

const rechazarCotizacionItem = async (item) => {
  const id = item.idCotizacion || item.id;
  if (!id) {
    toast('Error: ID de cotización no válido', 'error');
    return;
  }

  const { value: motivo } = await Swal.fire({
    title: 'Motivo del rechazo',
    input: 'textarea',
    inputPlaceholder: 'Escribe el motivo del rechazo...',
    showCancelButton: true,
    confirmButtonText: 'Rechazar',
    cancelButtonText: 'Cancelar'
  });

  if (motivo !== undefined) {
    try {
      const response = await rechazarCotizacionAPI(id, motivo || 'Sin motivo especificado');
      if (response.success) {
        toast('Cotización rechazada correctamente');
        await cargarDatos();
      } else {
        toast(response.error || 'Error al rechazar', 'error');
      }
    } catch (error) {
      toast('Error al rechazar', 'error');
    }
  }
};

// ============================================
// IMPRIMIR REPORTE
// ============================================
const imprimirReporte = () => {
  window.print();
};

// ============================================
// MODAL DE EDICIÓN
// ============================================
const abrirModalEdicion = async (item) => {
  console.log('✏️ Abriendo modal de edición para:', item);
  cotizacionEditando.value = item;
  
  // 👈 Si no hay datos maestros cargados, recargarlos
  if (clientes.value.length === 0 || productos.value.length === 0 || servicios.value.length === 0) {
    await cargarDatosMaestros();
  }
  
  const clienteEncontrado = clientes.value.find(c => c.idCliente === item.idCliente);
  if (clienteEncontrado) {
    clienteSeleccionado.value = clienteEncontrado;
  } else {
    clienteSeleccionado.value = {
      idCliente: item.idCliente,
      nombre: item.clienteNombre?.split(' ')[0] || '',
      apellido: item.clienteNombre?.split(' ').slice(1).join(' ') || '',
      documento: item.clienteDocumento || ''
    };
  }
  
  formEdicion.value = {
    idCotizacion: item.idCotizacion,
    idCliente: item.idCliente,
    fechaValidez: item.fechaValidez?.split('T')[0] || '',
    formaPago: item.formaPago || '',
    condicionesPago: item.condicionesPago || '',
    observaciones: item.observaciones || '',
    numeroCotizacion: item.numeroCotizacion || '',
    detalles: (item.detalles || []).map(d => {
      let productoSeleccionado = null;
      let servicioSeleccionado = null;
      
      if (d.tipoItem === 'producto' && d.idProducto) {
        productoSeleccionado = productos.value.find(p => p.idProducto === d.idProducto);
        if (!productoSeleccionado) {
          productoSeleccionado = { 
            idProducto: d.idProducto, 
            nombre: d.productoNombre || 'Producto', 
            precioVentaUsd: d.precioUnitarioUsd 
          };
        }
      }
      if (d.tipoItem === 'servicio' && d.idServicio) {
        servicioSeleccionado = servicios.value.find(s => s.idServicio === d.idServicio);
        if (!servicioSeleccionado) {
          servicioSeleccionado = { 
            idServicio: d.idServicio, 
            nombre: d.servicioNombre || 'Servicio', 
            precioUsd: d.precioUnitarioUsd 
          };
        }
      }
      
      return {
        idCotizacionDetalle: d.idCotizacionDetalle,
        tipoItem: d.tipoItem || 'servicio',
        idProducto: d.idProducto,
        idServicio: d.idServicio,
        idProductoServicios: d.idProductoServicios,
        productoSeleccionado: productoSeleccionado,
        servicioSeleccionado: servicioSeleccionado,
        descripcionManual: d.descripcionManual || '',
        cantidad: d.cantidad || 1,
        precioUnitarioUsd: d.precioUnitarioUsd || 0,
        descuentoPorcentaje: d.descuentoPorcentaje || 0,
        garantiaMeses: d.garantiaMeses || 0,
        garantiaDescripcion: d.garantiaDescripcion || '',
        tiempoEstimado: d.tiempoEstimado || '',
        observaciones: d.observaciones || ''
      };
    })
  };
  
  modalEdicionVisible.value = true;
};

const cerrarModalEdicion = () => {
  modalEdicionVisible.value = false;
  cotizacionEditando.value = null;
  clienteSeleccionado.value = null;
};

const agregarItemEdicion = () => {
  formEdicion.value.detalles.push({
    tipoItem: 'servicio',
    idProducto: null,
    idServicio: null,
    idProductoServicios: null,
    productoSeleccionado: null,
    servicioSeleccionado: null,
    cantidad: 1,
    precioUnitarioUsd: 0,
    descripcionManual: '',
    descuentoPorcentaje: 0,
    garantiaMeses: 0,
    garantiaDescripcion: '',
    tiempoEstimado: '',
    observaciones: ''
  });
};

const eliminarItemEdicion = (index) => {
  if (formEdicion.value.detalles.length > 1) {
    formEdicion.value.detalles.splice(index, 1);
  } else {
    toast('Debe tener al menos un item', 'warning');
  }
};

// ============================================
// CLIENTE - MODAL EDICIÓN (con recarga automática)
// ============================================
const abrirModalClienteEdicion = async () => {
  busquedaCliente.value = '';
  modalClienteVisible.value = true;
  
  // 👈 FIX: Si no hay clientes cargados, recargarlos AHORA
  if (clientes.value.length === 0) {
    cargandoClientesModal.value = true;
    try {
      console.log('⚠️ Clientes vacíos, recargando...');
      const res = await obtenerClientes();
      if (res.success) {
        clientes.value = res.data || [];
        console.log('✅ Clientes recargados:', clientes.value.length);
      } else {
        toast('No se pudieron cargar los clientes', 'error');
      }
    } catch (err) {
      console.error('Error recargando clientes:', err);
      toast('Error al cargar clientes', 'error');
    } finally {
      cargandoClientesModal.value = false;
    }
  }
};

const seleccionarClienteEdicion = (cliente) => {
  clienteSeleccionado.value = cliente;
  formEdicion.value.idCliente = cliente.idCliente;
  modalClienteVisible.value = false;
};

// ============================================
// PRODUCTO - MODAL EDICIÓN (con recarga automática)
// ============================================
const abrirModalProductoEdicion = async (index) => {
  itemIndexProducto.value = index;
  busquedaProducto.value = '';
  modalProductoVisible.value = true;
  
  // 👈 FIX: Si no hay productos cargados, recargarlos AHORA
  if (productos.value.length === 0) {
    cargandoProductosModal.value = true;
    try {
      console.log('⚠️ Productos vacíos, recargando...');
      const res = await obtenerProductos('todos');
      if (res.success) {
        productos.value = res.data || [];
        console.log('✅ Productos recargados:', productos.value.length);
      } else {
        toast('No se pudieron cargar los productos', 'error');
      }
    } catch (err) {
      console.error('Error recargando productos:', err);
      toast('Error al cargar productos', 'error');
    } finally {
      cargandoProductosModal.value = false;
    }
  }
};

const seleccionarProductoEdicion = (producto) => {
  const index = itemIndexProducto.value;
  if (index !== null) {
    const item = formEdicion.value.detalles[index];
    item.productoSeleccionado = producto;
    item.idProducto = producto.idProducto || producto.id_producto;
    item.precioUnitarioUsd = producto.precioVentaUsd || producto.precio_venta_usd || 0;
    item.descripcionManual = producto.descripcion || producto.nombre;
  }
  modalProductoVisible.value = false;
};

// ============================================
// SERVICIO - MODAL EDICIÓN (con recarga automática)
// ============================================
const abrirModalServicioEdicion = async (index) => {
  itemIndexServicio.value = index;
  busquedaServicio.value = '';
  modalServicioVisible.value = true;
  
  // 👈 FIX: Si no hay servicios cargados, recargarlos AHORA
  if (servicios.value.length === 0) {
    cargandoServiciosModal.value = true;
    try {
      console.log('⚠️ Servicios vacíos, recargando...');
      const res = await obtenerServicios('todos');
      if (res.success) {
        servicios.value = res.data || [];
        console.log('✅ Servicios recargados:', servicios.value.length);
      } else {
        toast('No se pudieron cargar los servicios', 'error');
      }
    } catch (err) {
      console.error('Error recargando servicios:', err);
      toast('Error al cargar servicios', 'error');
    } finally {
      cargandoServiciosModal.value = false;
    }
  }
};

const seleccionarServicioEdicion = (servicio) => {
  const index = itemIndexServicio.value;
  if (index !== null) {
    const item = formEdicion.value.detalles[index];
    item.servicioSeleccionado = servicio;
    item.idServicio = servicio.idServicio || servicio.id_servicio;
    item.precioUnitarioUsd = servicio.precioUsd || servicio.precio_usd || 0;
    item.descripcionManual = servicio.descripcion || servicio.nombre;
  }
  modalServicioVisible.value = false;
};

// ============================================
// GUARDAR EDICIÓN
// ============================================
const guardarEdicion = async () => {
  // Validaciones mínimas
  if (!formEdicion.value.idCliente) {
    toast('Debes seleccionar un cliente', 'warning');
    return;
  }
  if (!formEdicion.value.fechaValidez) {
    toast('Debes seleccionar una fecha de validez', 'warning');
    return;
  }
  if (formEdicion.value.detalles.length === 0) {
    toast('Debe tener al menos un item', 'warning');
    return;
  }

  guardandoEdicion.value = true;
  try {
    const data = {
      idCotizacion: formEdicion.value.idCotizacion,
      fechaValidez: new Date(formEdicion.value.fechaValidez).toISOString(),
      tasaBsUsd: 52.00,
      formaPago: formEdicion.value.formaPago || 'EFECTIVO',
      condicionesPago: formEdicion.value.condicionesPago || '',
      observaciones: formEdicion.value.observaciones || '',
      detalles: formEdicion.value.detalles.map(item => ({
        idCotizacionDetalle: item.idCotizacionDetalle || null,
        tipoItem: item.tipoItem,
        idProducto: item.idProducto ? parseInt(item.idProducto) : null,
        idServicio: item.idServicio ? parseInt(item.idServicio) : null,
        idProductoServicios: item.idProductoServicios ? parseInt(item.idProductoServicios) : null,
        descripcionManual: item.descripcionManual || '',
        cantidad: parseInt(item.cantidad) || 1,
        precioUnitarioUsd: parseFloat(item.precioUnitarioUsd) || 0,
        descuentoPorcentaje: parseFloat(item.descuentoPorcentaje) || 0,
        garantiaMeses: parseInt(item.garantiaMeses) || 0,
        garantiaDescripcion: item.garantiaDescripcion || '',
        tiempoEstimado: item.tiempoEstimado || '',
        observaciones: item.observaciones || '',
        eliminar: false
      }))
    };
    
    console.log('📤 Enviando actualización:', data);
    
    const response = await actualizarCotizacion(data);
    if (response.success) {
      toast('Cotización actualizada correctamente');
      cerrarModalEdicion();
      await cargarDatos();
    } else {
      toast(response.error || 'Error al actualizar', 'error');
    }
  } catch (error) {
    console.error('❌ Error:', error);
    toast(error.message || 'Error al actualizar', 'error');
  } finally {
    guardandoEdicion.value = false;
  }
};

// ============================================
// UTILIDADES
// ============================================
const getEstadoLabel = (estado) => {
  const labels = {
    borrador: 'Borrador',
    enviada: 'Enviada',
    aprobada: 'Aprobada',
    rechazada: 'Rechazada',
    expirada: 'Expirada',
    convertida: 'Convertida'
  };
  return labels[estado] || estado;
};

const getEstadoClass = (estado) => {
  const classes = {
    borrador: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
    enviada: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    aprobada: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
    rechazada: 'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400',
    expirada: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    convertida: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
  };
  return classes[estado] || 'bg-slate-100 text-slate-600';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const toast = (message, icon = 'success') => {
  Swal.fire({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    icon,
    title: message,
    customClass: { popup: 'rounded-2xl' }
  });
};

// ============================================
// LIFECYCLE
// ============================================
onMounted(async () => {
  console.log('🚀 Montando componente CotizacionesList...');
  await cargarDatosMaestros();
  await cargarDatos();
});
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
.custom-scrollbar::-webkit-scrollbar { height: 6px; width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; }

@keyframes zoomIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
.animate-zoomIn { animation: zoomIn 0.2s ease-out; }

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(15px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeInUp { animation: fadeInUp 0.4s ease-out forwards; }
.delay-100 { animation-delay: 0.1s; }

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
.animate-fadeIn { animation: fadeIn 0.3s ease-out; }
</style>